import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Send, MessageSquare, User, BookOpen, Award } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface FeedbackRecipient {
  id: string;
  name: string;
  role: string;
  type: 'mentor' | 'instructor' | 'admin' | 'coordinator';
  email?: string;
}

interface SentFeedback {
  id: string;
  recipient_name: string;
  recipient_role: string;
  subject: string;
  message: string;
  category: string;
  sent_at: string;
  status: 'sent' | 'read' | 'replied';
}

export function FeedbackCenter() {
  const [recipients, setRecipients] = useState<FeedbackRecipient[]>([]);
  const [sentFeedback, setSentFeedback] = useState<SentFeedback[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const [feedbackForm, setFeedbackForm] = useState({
    recipient_id: '',
    category: '',
    subject: '',
    message: '',
    anonymous: false
  });

  useEffect(() => {
    loadRecipients();
    loadSentFeedback();
  }, []);

  const loadRecipients = async () => {
    try {
      // Mock data - in real implementation would fetch mentors, instructors, and admins
      const mockRecipients: FeedbackRecipient[] = [
        {
          id: '1',
          name: 'Dr. Sarah Johnson',
          role: 'Mentor Teacher',
          type: 'mentor',
          email: 'sarah.johnson@school.edu'
        },
        {
          id: '2',
          name: 'Michael Chen',
          role: 'Professional Development Coordinator',
          type: 'coordinator',
          email: 'michael.chen@school.edu'
        },
        {
          id: '3',
          name: 'Jennifer Williams',
          role: 'Course Instructor',
          type: 'instructor',
          email: 'jennifer.williams@school.edu'
        },
        {
          id: '4',
          name: 'Principal Rodriguez',
          role: 'Building Principal',
          type: 'admin',
          email: 'principal@school.edu'
        }
      ];
      
      setRecipients(mockRecipients);
    } catch (error) {
      console.error('Error loading recipients:', error);
    }
  };

  const loadSentFeedback = async () => {
    try {
      // Mock data - in real implementation would fetch from database
      const mockFeedback: SentFeedback[] = [
        {
          id: '1',
          recipient_name: 'Dr. Sarah Johnson',
          recipient_role: 'Mentor Teacher',
          subject: 'Thank you for the classroom management tips',
          message: 'The strategies you shared last week have been incredibly helpful...',
          category: 'appreciation',
          sent_at: '2024-01-20T10:30:00Z',
          status: 'read'
        },
        {
          id: '2',
          recipient_name: 'Michael Chen',
          recipient_role: 'PD Coordinator',
          subject: 'Suggestion for future workshops',
          message: 'I would love to see more hands-on technology integration sessions...',
          category: 'suggestion',
          sent_at: '2024-01-18T14:15:00Z',
          status: 'replied'
        }
      ];
      
      setSentFeedback(mockFeedback);
    } catch (error) {
      console.error('Error loading sent feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendFeedback = async () => {
    try {
      if (!feedbackForm.recipient_id || !feedbackForm.subject || !feedbackForm.message) {
        toast({
          title: "Validation Error", 
          description: "Please fill in all required fields",
          variant: "destructive"
        });
        return;
      }

      const recipient = recipients.find(r => r.id === feedbackForm.recipient_id);
      
      // Mock sending feedback - in real implementation would save to database
      const newFeedback: SentFeedback = {
        id: Date.now().toString(),
        recipient_name: recipient?.name || 'Unknown',
        recipient_role: recipient?.role || 'Unknown',
        subject: feedbackForm.subject,
        message: feedbackForm.message,
        category: feedbackForm.category,
        sent_at: new Date().toISOString(),
        status: 'sent'
      };

      setSentFeedback([newFeedback, ...sentFeedback]);
      
      toast({
        title: "Feedback Sent!",
        description: `Your message has been sent to ${recipient?.name}`,
      });

      // Reset form
      setFeedbackForm({
        recipient_id: '',
        category: '',
        subject: '',
        message: '',
        anonymous: false
      });
      
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error sending feedback:', error);
      toast({
        title: "Error sending feedback",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'appreciation':
        return <Award className="h-4 w-4" />;
      case 'question':
        return <MessageSquare className="h-4 w-4" />;
      case 'suggestion':
        return <BookOpen className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'appreciation':
        return <Badge className="bg-green-100 text-green-800">Appreciation</Badge>;
      case 'question':
        return <Badge className="bg-blue-100 text-blue-800">Question</Badge>;
      case 'suggestion':
        return <Badge className="bg-purple-100 text-purple-800">Suggestion</Badge>;
      case 'concern':
        return <Badge className="bg-orange-100 text-orange-800">Concern</Badge>;
      default:
        return <Badge variant="secondary">Feedback</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge variant="outline">Sent</Badge>;
      case 'read':
        return <Badge className="bg-blue-100 text-blue-800">Read</Badge>;
      case 'replied':
        return <Badge className="bg-green-100 text-green-800">Replied</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getRecipientTypeIcon = (type: string) => {
    switch (type) {
      case 'mentor':
        return <User className="h-4 w-4 text-blue-500" />;
      case 'instructor':
        return <BookOpen className="h-4 w-4 text-green-500" />;
      case 'admin':
        return <Award className="h-4 w-4 text-purple-500" />;
      default:
        return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading feedback center...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Feedback Center</h2>
          <p className="text-muted-foreground">Share feedback with your mentors, instructors, and administrators</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Send className="h-4 w-4 mr-2" />
              Send Feedback
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Send Feedback</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="recipient">Send to</Label>
                  <Select 
                    value={feedbackForm.recipient_id}
                    onValueChange={(value) => setFeedbackForm({...feedbackForm, recipient_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose recipient" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipients.map((recipient) => (
                        <SelectItem key={recipient.id} value={recipient.id}>
                          <div className="flex items-center gap-2">
                            {getRecipientTypeIcon(recipient.type)}
                            <div>
                              <div className="font-medium">{recipient.name}</div>
                              <div className="text-sm text-muted-foreground">{recipient.role}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select 
                    value={feedbackForm.category}
                    onValueChange={(value) => setFeedbackForm({...feedbackForm, category: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appreciation">Appreciation</SelectItem>
                      <SelectItem value="question">Question</SelectItem>
                      <SelectItem value="suggestion">Suggestion</SelectItem>
                      <SelectItem value="concern">Concern</SelectItem>
                      <SelectItem value="general">General Feedback</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={feedbackForm.subject}
                  onChange={(e) => setFeedbackForm({...feedbackForm, subject: e.target.value})}
                  placeholder="Enter subject line"
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={feedbackForm.message}
                  onChange={(e) => setFeedbackForm({...feedbackForm, message: e.target.value})}
                  placeholder="Write your feedback message..."
                  rows={6}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSendFeedback}>
                  <Send className="h-4 w-4 mr-2" />
                  Send Feedback
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Feedback Recipients */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Quick Send To</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {recipients.map((recipient) => (
            <Card key={recipient.id} className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setFeedbackForm({...feedbackForm, recipient_id: recipient.id});
                    setIsDialogOpen(true);
                  }}>
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  {getRecipientTypeIcon(recipient.type)}
                </div>
                <h4 className="font-medium">{recipient.name}</h4>
                <p className="text-sm text-muted-foreground">{recipient.role}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Sent Feedback History */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Recent Feedback</h3>
        {sentFeedback.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h4 className="text-lg font-medium">No feedback sent yet</h4>
              <p className="text-muted-foreground">Start sharing your thoughts and appreciation!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sentFeedback.map((feedback) => (
              <Card key={feedback.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-base">
                        {getCategoryIcon(feedback.category)}
                        {feedback.subject}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        To: {feedback.recipient_name} ({feedback.recipient_role})
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getCategoryBadge(feedback.category)}
                      {getStatusBadge(feedback.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-2 line-clamp-2">{feedback.message}</p>
                  <p className="text-xs text-muted-foreground">
                    Sent {new Date(feedback.sent_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}